import { Dexie, Table } from 'dexie';
import { FailedRequestType } from 'services/enums';
import { SyncingStatus } from 'services/enums/syncingStatus';
import { AppSetting, CatchWeight, Company, EquipmentShortReason, EquipmentType, SyncStatus, FilteredLicensePlate, LicensePlate, LicensePlateItem, LineItem, LineItemBarcode, Mispick, NavigationEntry, NavigationRoute, Order, OrderItem, OrderItemChange, OrderLicensePlate, ReconcileReason, RefusalReason, ReturnChangeReason, ReturnItem, Signature, Stop, StopEquipment, Trip, User, VerifyMultipleItemsReason, CompleteStop, OrderItemChangeImage } from 'services/types';
import { CapturedAppStateHolder } from 'services/types/capturedAppStateHolder';
import { CompanyEmail } from 'services/types/companyEmail';
import { DbOperation } from 'types';
import { DbContextError } from 'types/dbContextError';

export enum DbWorkerMessageType {
    Get = 'GET',
    Put = 'PUT',
    BulkPut = 'BULK_PUT',
    Remove = 'REMOVE',
    BulkRemove = 'BULK_REMOVE',
    Set = 'SET',
    DeleteDb = 'DELETE_DB'
}


export type DbWorkerMessage = {
    type: DbWorkerMessageType,
    tableName: string,
    payload: any
}

export type DbSchema = {
    [tableName: string]: string | null
}

class DbContext extends Dexie {

    trip: Table<Trip, number>
    stop: Table<Stop, number>
    company: Table<Company, string>
    order: Table<Order, number>
    orderItem: Table<OrderItem, number>
    lineItem: Table<LineItem, number>
    user: Table<User, string>
    navigationStack: Table<NavigationEntry, number>
    navigationRoute: Table<NavigationRoute, number>
    verifyMultipleItemsReason: Table<VerifyMultipleItemsReason, number>
    reconcileReason: Table<ReconcileReason, number>
    refusalReason: Table<RefusalReason, number>
    palletShortReason: Table<EquipmentShortReason, number>
    returnChangeReason: Table<ReturnChangeReason, number>
    orderItemChange: Table<OrderItemChange, number>
    catchWeight: Table<CatchWeight, number>
    licensePlate: Table<LicensePlate, number>
    licensePlateItem: Table<LicensePlateItem, number>
    equipmentType: Table<EquipmentType, number>
    stopEquipment: Table<StopEquipment, number>
    orderLicensePlate: Table<OrderLicensePlate, number>
    lineItemBarcode: Table<LineItemBarcode, number>
    signature: Table<Signature, number>
    mispicks: Table<Mispick, number>
    setting: Table<AppSetting, string>
    returnItem: Table<ReturnItem, number>
    syncStatus: Table<SyncStatus, number>
    filteredLicensePlates: Table<FilteredLicensePlate, number>
    dbOperations: Table<DbOperation, number>
    capturedAppStateHolder: Table<CapturedAppStateHolder, number>
    orderItemChangeImage: Table<OrderItemChangeImage, number>
    companyEmail: Table<CompanyEmail, number>
    companyEmailAdditions: Table<CompanyEmail, number>
    companyEmailDeletions: Table<CompanyEmail, number>
    [key: string]: any

    constructor() {
        super('DTI.Clients.Driver')

        let stores: DbSchema = {
            trip: `id`,
            stop: `id`,
            company: `id`,
            order: `id`,
            orderItem: `id`,
            lineItem: `id`,
            lineItemBarcode: 'id',
            user: 'preferred_username',
            navigationStack: '++id',
            verifyMultipleItemsReason: 'id',
            reconcileReason: 'id',
            refusalReason: 'id',
            palletShortReason: 'id',
            returnChangeReason: 'id',
            orderItemChange: '++id',
            catchWeight: 'id',
            licensePlate: 'id',
            licensePlateItem: 'id',
            equipmentType: 'id',
            stopEquipment: 'id',
            orderLicensePlate: 'id',
            signature: 'id',
            mispicks: 'id',
            setting: 'id',
            failedRequest: 'stopId',
            returnItem: 'id',
            filteredLicensePlates: 'id'
        }

        this.version(10).stores(stores)

        stores = {
            ...stores,
            failedRequest: null,
            failedRequestTemp: '++id',
            navigationStack: null,
            navigationStackTemp: 'id'
        }
        this.version(11).stores(stores).upgrade(trans => {
            trans.table('failedRequest').toArray().then(requests => {
                let newId = 0
                const newRequests = requests.map(request => {
                    return {
                        id: newId++,
                        type: FailedRequestType.StopCompletion,
                        data: request.stopId
                    }
                })
                trans.table('failedRequestTemp').bulkAdd(newRequests)
            })
            trans.table('navigationStack').toArray().then(stackEntries => {
                let newId = 0
                const newEntries = stackEntries.map(entry => ({ ...entry, id: newId++ }))
                trans.table('navigationStackTemp').bulkAdd(newEntries)
            })
        })

        stores = {
            ...stores,
            failedRequestTemp: null,
            failedRequest: '++id',
            navigationStackTemp: null,
            navigationStack: 'id'
        }
        this.version(12).stores(stores).upgrade(trans => {
            trans.table('failedRequestTemp').toArray().then(requests => {
                trans.table('failedRequest').bulkAdd(requests).then(() => {
                    trans.table('navigationStackTemp').toArray().then(stackEntries => {
                        trans.table('navigationStack').bulkAdd(stackEntries)
                    })
                })
            })
        })

        stores = {
            ...stores,
            user: null,
            userTemp: 'id',
            orderItemChange: null,
            orderItemChangeTemp: 'id'
        }
        this.version(13).stores(stores).upgrade(trans => {
            trans.table('user').toArray().then(users => {
                let newId = 0
                const newUsers = users.map(user => ({
                    id: newId++,
                    ...user
                }))
                trans.table('userTemp').bulkAdd(newUsers)
            })

            trans.table('orderItemChange').toArray().then(changes => {
                let newId = 0
                const newChanges = changes.map(change => ({
                    id: newId++,
                    ...change
                }))
                trans.table('orderItemChangeTemp').bulkAdd(newChanges)
            })
        })

        stores = {
            ...stores,
            user: 'id',
            userTemp: null,
            orderItemChange: 'id',
            orderItemChangeTemp: null
        }
        this.version(14).stores(stores).upgrade(trans => {
            trans.table('userTemp').toArray().then(users => {
                trans.table('user').bulkAdd(users).then(() => {
                    trans.table('orderItemChangeTemp').toArray().then(changes => {
                        trans.table('orderItemChange').bulkAdd(changes)
                    })
                })
            })
        })

        stores = {
            ...stores,
            navigationRoute: 'id'
        }

        this.version(15).stores(stores)

        stores = {
            ...stores,
            dbOperations: '++id'
        }

        this.version(16).stores(stores)

        stores = {
            ...stores,
            failedRequest: null,
            stopSyncStatus: 'id'
        }
        this.version(17).stores(stores).upgrade(trans => {
            trans.table('failedRequest').toArray().then(requests => {
                const newRecord = requests.map(request => ({
                    data: null,
                    id: request.data,
                    status: SyncingStatus.FailedToSync,
                    type: request.type
                }))
                trans.table('stopSyncStatus').bulkAdd(newRecord)
            })
        })

        stores = {
            ...stores,
            capturedAppStateHolder: '++id'
        }

        this.version(18).stores(stores)

        stores = {
            ...stores,
            stopSyncStatus: null,
            syncStatus: 'id'
        }

        this.version(19).stores(stores).upgrade(trans => {
            trans.table('stopSyncStatus').toArray().then(statuses => {
                const newRecord = statuses.map(status => ({
                    data: status.data,
                    id: status.data?.appointment?.id,
                    status: SyncingStatus.FailedToSync,
                    type: status.type
                }))
                trans.table('syncStatus').bulkAdd(newRecord)
            })
        })
        
        stores = {
            ...stores,
            uploadedImage: '++id'
        }

        this.version(20).stores(stores)

        stores = {
            ...stores,
            uploadedImage: null,
            orderItemChangeImage: '++id'
        }

        this.version(21).stores(stores)

        stores = {
            ...stores,
            companyEmail: 'id'
        }

        this.version(22).stores(stores)

        stores = {
            ...stores,
            companyEmailAdditions: 'id',
            companyEmailDeletions: 'id'
        }

        this.version(23).stores(stores)

        this.trip = this.table('trip')
        this.stop = this.table('stop')
        this.company = this.table('company')
        this.order = this.table('order')
        this.orderItem = this.table('orderItem')
        this.lineItem = this.table('lineItem')
        this.user = this.table('user')
        this.navigationStack = this.table('navigationStack')
        this.navigationRoute = this.table('navigationRoute')
        this.verifyMultipleItemsReason = this.table('verifyMultipleItemsReason')
        this.reconcileReason = this.table('reconcileReason')
        this.refusalReason = this.table('refusalReason')
        this.palletShortReason = this.table('palletShortReason')
        this.returnChangeReason = this.table('returnChangeReason')
        this.orderItemChange = this.table('orderItemChange')
        this.catchWeight = this.table('catchWeight')
        this.licensePlate = this.table('licensePlate')
        this.licensePlateItem = this.table('licensePlateItem')
        this.equipmentType = this.table('equipmentType')
        this.stopEquipment = this.table('stopEquipment')
        this.orderLicensePlate = this.table('orderLicensePlate')
        this.lineItemBarcode = this.table('lineItemBarcode')
        this.signature = this.table('signature')
        this.mispicks = this.table('mispicks')
        this.setting = this.table('setting')
        this.syncStatus = this.table('syncStatus')
        this.returnItem = this.table('returnItem')
        this.filteredLicensePlates = this.table('filteredLicensePlates')
        this.dbOperations = this.table('dbOperations')
        this.capturedAppStateHolder = this.table('capturedAppStateHolder')
        this.orderItemChangeImage = this.table('orderItemChangeImage')
        this.companyEmail = this.table('companyEmail')
        this.companyEmailAdditions = this.table('companyEmailAdditions')
        this.companyEmailDeletions = this.table('companyEmailDeletions')
    }
}


const dbContext = new DbContext()
const workerContext: Worker = self as any

export const getDatabaseErrorIfDbContextCannotBeOpened = async (): Promise<DbContextError | null> => {
    try {
        await dbContext.open();
    }
    catch (error: any) {

        let errorMessage = JSON.stringify(error.inner);
        errorMessage += JSON.stringify(error.message);
        errorMessage += JSON.stringify(error.name);

        let tableNames = dbContext.tables.map(x => x.name).join(', ')

        let dbContextError: DbContextError = {
            errorMessage: errorMessage,
            allTableNames: tableNames,
            currentVersionNumber: dbContext.verno
        };

        return dbContextError;
    }

    return null;
}

const get = (tableName: string) => {
    dbContext.open().then(() => {
        dbContext[tableName]?.toArray().then((response: any) => {
            workerContext.postMessage({
                type: DbWorkerMessageType.Get,
                tableName: tableName,
                payload: response
            })
        })
    })
}

const put = (tableName: string, item: any) => {
    dbContext.open().then(() => {
        dbContext[tableName].put(item)
    })
}

const bulkPut = (tableName: string, items: any[]) => {
    dbContext.open().then(() => {
        dbContext[tableName].bulkPut(items)
    })
}

const remove = (tableName: string, itemId: string | number) => {
    dbContext.open().then(() => {
        dbContext[tableName].delete(itemId)
    })
}

const bulkRemove = (tableName: string, items: any[]) => {
    dbContext.open().then(() => {
        dbContext[tableName].bulkDelete(items)
    })
}

const set = (tableName: string, items: any[]) => {
    dbContext.open().then(() => {
        dbContext[tableName].clear().then(() => {
            dbContext[tableName].bulkPut(items)
        })
    })
}

const deleteDb = () => {
    dbContext.open().then(() => {
        dbContext.delete().then(() => {
            workerContext.postMessage({ type: DbWorkerMessageType.DeleteDb })
        })
    })
}

workerContext.addEventListener('message', (e) => {
    const message = e.data as DbWorkerMessage
    switch (message.type) {
        case DbWorkerMessageType.Get: get(message.tableName); break;
        case DbWorkerMessageType.Put: put(message.tableName, message.payload); break;
        case DbWorkerMessageType.BulkPut: bulkPut(message.tableName, message.payload); break;
        case DbWorkerMessageType.Remove: remove(message.tableName, message.payload); break;
        case DbWorkerMessageType.BulkRemove: bulkRemove(message.tableName, message.payload); break;
        case DbWorkerMessageType.Set: set(message.tableName, message.payload); break;
        case DbWorkerMessageType.DeleteDb: deleteDb(); break;
    }
});

workerContext.addEventListener('exit', () => { process.exit(0); }, false);